import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

// Build the API base URL. In production we always call the backend on the
// SAME origin as the frontend, because deployment systems often bake in a
// stale REACT_APP_BACKEND_URL at compile time. In local dev we respect the
// env var so developers can point at a remote backend while running
// `yarn start` on localhost.
function resolveApiBase() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === "undefined") return envUrl || "";
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host === "127.0.0.1";
  if (isLocal && envUrl) return envUrl;
  // any deployed domain (preview + prod + custom): use same origin
  return "";
}

const BACKEND_URL = resolveApiBase();
export const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies and keep token
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem("spv_session_token");

  const authHeaders = useCallback(() => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers: authHeaders() });
    } catch {}
    localStorage.removeItem("spv_session_token");
    setUser(null);
    window.location.href = "/";
  };

  const setRole = async (role, company, country) => {
    const res = await axios.post(
      `${API}/auth/set-role`,
      { role, company, country },
      { withCredentials: true, headers: authHeaders() }
    );
    setUser(res.data);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, logout, setRole, checkAuth, authHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
